import { useSelector } from 'react-redux'
import { Text } from 'common-components'

import { aiLibSelectors } from 'store/features/aiLibVideo/selectors'
import { aiLibThunks } from 'store/features/aiLibVideo/thunks'
import * as Styled from './styled'

import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { memo, useCallback, useMemo } from 'react'
import OptionDropdown from './OptionDropdown'
import { FILTER_DROPDOWNS } from './const'
import HistoryScopeCheckbox from '../HistoryCheckbox'
import { replaceTranslationVariables } from 'utils/text-transformation'
import { dispatch } from 'store'

const FiltersSection = () => {
  const aiVideoModels = useSelector(aiLibSelectors.allModels)

  const availableDurationOptions = useSelector(
    aiLibSelectors.availableDurationOptions
  )
  const availableAspectRatioOptions = useSelector(
    aiLibSelectors.availableAspectRatioOptions
  )
  const availableQualityOptions = useSelector(
    aiLibSelectors.availableQualityOptions
  )

  const selectedDuration = useSelector(aiLibSelectors.selectedDuration)
  const selectedAspectRatio = useSelector(aiLibSelectors.selectedAspectRatio)
  const selectedQuality = useSelector(aiLibSelectors.selectedQuality)
  const selectedModelCode = useSelector(aiLibSelectors.selectedModelCode)

  const handleDurationChange: (value: string | number | null) => void =
    useCallback((value: string | number | null) => {
      dispatch(aiLibThunks.setFilter({ duration: value }))
    }, [])

  const handleAspectRatioChange: (value: string | number | null) => void =
    useCallback((value: string | number | null) => {
      dispatch(aiLibThunks.setFilter({ aspectRatio: value }))
    }, [])

  const handleQualityChange: (value: string | number | null) => void =
    useCallback((value: string | number | null) => {
      dispatch(aiLibThunks.setFilter({ quality: value }))
    }, [])

  const handleModelChange = useCallback((modelCode: string | null) => {
    dispatch(aiLibThunks.changeModel(modelCode))
  }, [])

  const dropdownDataMap = useMemo(
    () => ({
      duration: {
        options: availableDurationOptions,
        selectedValue: selectedDuration,
        onSelect: handleDurationChange
      },
      aspectRatio: {
        options: availableAspectRatioOptions,
        selectedValue: selectedAspectRatio,
        onSelect: handleAspectRatioChange
      },
      quality: {
        options: availableQualityOptions,
        selectedValue: selectedQuality,
        onSelect: handleQualityChange
      }
    }),
    [
      availableDurationOptions,
      selectedDuration,
      handleDurationChange,
      availableAspectRatioOptions,
      selectedAspectRatio,
      handleAspectRatioChange,
      availableQualityOptions,
      selectedQuality,
      handleQualityChange
    ]
  )

  const isLoading = useSelector(aiLibSelectors.isInitialLoading)
  const isHistoryScopedToCurrentProject = useSelector(
    aiLibSelectors.isHistoryScopedToCurrentProject
  )

  const onToggleScope = useCallback((next: boolean) => {
    dispatch(aiLibThunks.setFilter({ isHistoryScopedToCurrentProject: next }))
  }, [])

  return (
    <Styled.ModelSection>
      <Text type="h4" fontWeight="500" color="secondary">
        {translate(T.MODEL_LABEL)}
      </Text>
      <Styled.DropdownWrapper>
        <Styled.VideoModelDropdown
          models={aiVideoModels}
          selectedModelCode={selectedModelCode}
          onModelSelect={handleModelChange}
        />
        <Styled.ModelOptionsWrapper>
          {FILTER_DROPDOWNS.map(({ key, labelKey, className }) => {
            const { options, selectedValue, onSelect } = dropdownDataMap[key]
            return (
              <Styled.DropdownContainer key={key}>
                <Styled.DropdownLabel>
                  {translate(labelKey)}
                </Styled.DropdownLabel>
                <OptionDropdown
                  className={className}
                  label={translate(T.ALL_LABEL)}
                  options={options}
                  selectedValue={selectedValue}
                  onSelect={onSelect}
                />
              </Styled.DropdownContainer>
            )
          })}
        </Styled.ModelOptionsWrapper>
      </Styled.DropdownWrapper>
      <Styled.ScopeCheckboxWrapper>
        <HistoryScopeCheckbox
          className="scope-checkbox-label"
          label={replaceTranslationVariables(
            translate(T.CREATED_IN_THIS_PROJECT_LABEL),
            [translate(T.VIDEOS_LABEL)]
          )}
          isChecked={isHistoryScopedToCurrentProject}
          onToggle={onToggleScope}
          isDisabled={isLoading}
        />
      </Styled.ScopeCheckboxWrapper>
    </Styled.ModelSection>
  )
}

export default memo(FiltersSection)
